import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Phone, PhoneOff, Mic, MicOff, MessageCircle, Mail, MessageSquare, Send, CreditCard, Calendar, Sparkles, ShieldCheck, AlertTriangle, FileText, ChevronRight, CheckCheck, Clock, Wallet, User, Languages, Bookmark, Headphones, Bot } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { StatusChip } from '@/components/shared/StatusChip';
import { ComplianceBadge } from '@/components/shared/ComplianceBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { useApp } from '@/context/AppContext';
import { formatSAR, formatDate, formatDateTime, timeAgo } from '@/lib/format';
import { playAudio, stopSpeaking, describeVoice, isVoiceSupported, warmVoices } from '@/lib/voice';
import { DEBTOR360_SCRIPT, debtor360DebtorAudioId, profileKey } from '@/data/voiceLines';
import { SmartSummary } from '@/components/debtor/SmartSummary';
import { ResolutionOptions } from '@/components/debtor/ResolutionOptions';
import { ComplianceGate } from '@/components/shared/ComplianceGate';
import { evaluateAction } from '@/lib/compliance';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const SCRIPT_LINES = DEBTOR360_SCRIPT;

export default function Debtor360() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { debtors, updateDebtor, pushEvent } = useApp();
  const d = debtors.find((x) => x.caseId === caseId) || debtors[0];
  usePageTitle(d ? `${d.name} · ${d.caseId}` : 'Debtor 360');

  const [tab, setTab] = useState('overview');
  const [callActive, setCallActive] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [transcriptStep, setTranscriptStep] = useState(0);
  const [waMessages, setWaMessages] = useState([
    { who: 'system', text: 'Conversation opened • 24h window active', ts: '− 18m' },
    { who: 'agent', text: 'مساء الخير أستاذ فهد، أرسلت لك تفاصيل خطة السداد.', ts: '− 12m', state: 'read' },
  ]);
  const [waDraft, setWaDraft] = useState('');

  // Payment plan builder state
  const [installments, setInstallments] = useState(6);
  const [downPct, setDownPct] = useState(10);
  const monthly = d ? Math.round(((d.amount * (1 - downPct / 100)) / installments)) : 0;
  const downPayment = d ? Math.round(d.amount * (downPct / 100)) : 0;

  useEffect(() => {
    if (!callActive) return;
    const id = setInterval(() => setCallSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [callActive]);

  useEffect(() => {
    if (!callActive) { setTranscriptStep(0); stopSpeaking(); return; }
    let cancelled = false;
    const debtorProfile = profileKey(d.nationality, d.gender);
    (async () => {
      for (let i = 0; i < SCRIPT_LINES.length; i++) {
        if (cancelled) return;
        setTranscriptStep(i + 1);
        const line = SCRIPT_LINES[i];
        const audioId = line.profilePer
          ? debtor360DebtorAudioId(line.id, debtorProfile)
          : line.id;
        await playAudio(audioId);
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 350));
      }
    })();
    return () => { cancelled = true; stopSpeaking(); };
  }, [callActive, d.nationality, d.gender]);

  if (!d) return null;

  const startCall = () => {
    warmVoices();
    setCallActive(true); setCallSeconds(0);
    toast.success('Call connected', { description: 'Live transcript & AI voice playback started' });
    pushEvent({ type: 'CallStarted', src: 'Telephony', dst: 'Alwasl Recovery OS', status: 'success', payload: `${d.caseId} → ${d.mobileMasked}`, caseId: d.caseId });
  };
  const endCall = () => {
    setCallActive(false);
    stopSpeaking();
    toast.message('Call ended', { description: `Duration ${Math.floor(callSeconds / 60)}m ${callSeconds % 60}s • Disposition: Right-Party Contact` });
    pushEvent({ type: 'CallEnded', src: 'Telephony', dst: 'Alwasl Recovery OS', status: 'success', payload: `duration ${callSeconds}s, disposition RPC`, caseId: d.caseId });
  };

  const sendWA = () => {
    if (!waDraft.trim()) return;
    const msg = { who: 'agent', text: waDraft, ts: 'now', state: 'sent' };
    setWaMessages((m) => [...m, msg]);
    setWaDraft('');
    pushEvent({ type: 'WhatsAppSent', src: 'Alwasl Recovery OS', dst: 'WhatsApp Cloud', status: 'success', payload: msg.text.slice(0, 60), caseId: d.caseId });
    setTimeout(() => setWaMessages((m) => m.map((x, i) => i === m.length - 1 ? { ...x, state: 'delivered' } : x)), 500);
    setTimeout(() => setWaMessages((m) => m.map((x, i) => i === m.length - 1 ? { ...x, state: 'read' } : x)), 1400);
    setTimeout(() => {
      setWaMessages((m) => [...m, { who: 'debtor', text: 'تمام، سُرسل الدفعة الأولى اليوم. (OK, I will send the first payment today.)', ts: 'now' }]);
    }, 2800);
  };

  const sendPaymentLink = () => {
    toast.success('SADAD bill created', { description: `Reference SAD-${77000 + Math.floor(Math.random() * 999)} • SAR ${monthly.toLocaleString()} due` });
    pushEvent({ type: 'SADADBillCreated', src: 'Alwasl Recovery OS', dst: 'SADAD Mock', status: 'success', payload: `SAR ${monthly} bill issued`, caseId: d.caseId });
    setWaMessages((m) => [...m, { who: 'agent', text: `رابط الدفع للدفعة الأولى: pay.alwasl.demo/SAD-${77000 + Math.floor(Math.random() * 999)}`, ts: 'now', state: 'sent' }]);
  };

  const createPTP = () => {
    updateDebtor(d.caseId, { status: 'Promise to Pay' });
    toast.success('Promise-to-pay recorded', { description: `SAR ${monthly.toLocaleString()} due ${formatDate(new Date(Date.now() + 7 * 86400000).toISOString())}` });
    pushEvent({ type: 'PromiseCreated', src: 'Alwasl Recovery OS', dst: 'Audit', status: 'success', payload: `PTP SAR ${monthly}`, caseId: d.caseId });
  };

  const escalateLegal = () => {
    updateDebtor(d.caseId, { status: 'Legal Candidate' });
    toast.message('Case escalated to Legal', { description: 'Routed to Legal Workbench for readiness check' });
    pushEvent({ type: 'LegalEscalation', src: 'Agent Workspace', dst: 'Legal Workbench', status: 'success', payload: `${d.caseId} flagged`, caseId: d.caseId });
    navigate('/app/legal');
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Breadcrumb / Back */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/agent')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to queue
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">SLA <span className="font-mono-num ml-1">{formatDateTime(d.slaDue)}</span></Badge>
          <Button variant="outline" size="sm" onClick={escalateLegal} className="text-secondary">
            <FileText className="h-4 w-4 mr-1.5" /> Escalate to Legal
          </Button>
        </div>
      </div>

      {/* Header hero card */}
      <div className="relative surface-card overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-onyx via-onyx to-onyx/95 pattern-arabesque" />
        <div className="relative p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 text-background">
          <div className="lg:col-span-5 flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center text-2xl font-display font-semibold text-primary">
              {d.name.split(' ').map((w) => w[0]).join('')}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-primary">{d.caseId} • {d.debtorId}</p>
              <h2 className="text-2xl font-semibold mt-0.5">{d.name}</h2>
              <div className="flex items-center gap-3 mt-1.5 text-[12px] text-background/70">
                <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> ID {d.nationalIdMasked}</span>
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {d.mobileMasked}</span>
                <span className="inline-flex items-center gap-1"><Languages className="h-3 w-3" /> {d.language}</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/15 border border-primary/30 text-primary text-[11px]">
                  {d.nationalityLabel} • {d.gender === 'F' ? 'Female' : 'Male'}
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: 'Outstanding', v: formatSAR(d.amount), tone: 'gold' },
              { l: 'Delinquency', v: `${d.delinquencyDays} days` },
              { l: 'Propensity', v: `${d.scores.propensity}` },
              { l: 'Legal risk', v: `${d.scores.legalRisk}` },
            ].map((s) => (
              <div key={s.l} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-background/55">{s.l}</p>
                <p className={cn('font-mono-num text-lg font-semibold mt-0.5', s.tone === 'gold' ? 'text-primary' : 'text-background')}>{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Smart case summary — new */}
      <SmartSummary debtor={d} />

      {/* Next best action banner */}
      <div className="surface-card p-4 flex flex-col md:flex-row md:items-center gap-3 border-primary/40 bg-primary/5">
        <div className="flex items-center gap-3 flex-1">
          <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Next best action</p>
            <p className="text-sm font-medium text-foreground">{d.nextBestAction}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Based on contactability {d.scores.contactability} • propensity {d.scores.propensity} • SAR {d.amount.toLocaleString()} exposure</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ComplianceGate verdict={evaluateAction(d, 'call')}>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setTab('comms')} data-testid="debtor360-take-action">Take action</Button>
          </ComplianceGate>
        </div>
      </div>

      {/* Resolution options comparator — new */}
      <ResolutionOptions debtor={d} onPick={(opt) => {
        if (opt.id === 'plan' || opt.id === 'hardship') setTab('plan');
        if (opt.id === 'ptp') setTab('plan');
        if (opt.id === 'pay_now' || opt.id === 'settle') setTab('comms');
        pushEvent({ type: 'ResolutionPicked', src: 'Debtor 360', dst: 'Alwasl Recovery OS', status: 'success', payload: opt.id, caseId: d.caseId });
      }} />

      {/* Main two-column workspace */}
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-muted h-9">
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              <TabsTrigger value="comms" className="text-xs">Communications</TabsTrigger>
              <TabsTrigger value="plan" className="text-xs">Payment plan</TabsTrigger>
              <TabsTrigger value="timeline" className="text-xs">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* Scores + risk */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="surface-card p-5">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Risk & scores</p>
                  <div className="mt-3 space-y-3">
                    <ScoreBar label="Priority" value={d.scores.priority} tone="primary" />
                    <ScoreBar label="Payment propensity" value={d.scores.propensity} tone="success" />
                    <ScoreBar label="Contactability" value={d.scores.contactability} tone="info" />
                    <ScoreBar label="Legal risk" value={d.scores.legalRisk} tone="destructive" />
                  </div>
                </div>
                <div className="surface-card p-5">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Debt account</p>
                  <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Client</dt><dd>{d.client}</dd>
                    <dt className="text-muted-foreground">Product</dt><dd>{d.product}</dd>
                    <dt className="text-muted-foreground">Outstanding</dt><dd className="font-mono-num font-semibold">{formatSAR(d.amount)}</dd>
                    <dt className="text-muted-foreground">Delinquency</dt><dd>{d.delinquencyDays} days</dd>
                    <dt className="text-muted-foreground">Last payment</dt><dd>{formatDate(d.lastPayment)}</dd>
                    <dt className="text-muted-foreground">Segment</dt><dd><Badge variant="outline" className="text-[10px]">{d.segment}</Badge></dd>
                  </dl>
                </div>
              </div>
              <div className="surface-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Consent & contact preference</p>
                  <Badge variant="outline" className="text-[10px]">PDPL: minimized</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { k: 'Call', v: d.consent.call, icon: Phone },
                    { k: 'WhatsApp', v: d.consent.whatsapp, icon: MessageCircle },
                    { k: 'SMS', v: d.consent.sms, icon: MessageSquare },
                    { k: 'Email', v: d.consent.email, icon: Mail },
                  ].map((c) => (
                    <div key={c.k} className={cn('flex items-center justify-between px-3 py-2 rounded-lg border text-xs', c.v ? 'border-success/40 bg-success/10' : 'border-destructive/30 bg-destructive/10')}>
                      <span className="inline-flex items-center gap-1.5"><c.icon className="h-3.5 w-3.5" />{c.k}</span>
                      <span className={cn('font-medium', c.v ? 'text-success' : 'text-destructive')}>{c.v ? 'Allowed' : 'Opted-out'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="comms" className="mt-4 space-y-4">
              <CallConsole d={d} callActive={callActive} callSeconds={callSeconds} muted={muted} setMuted={setMuted} startCall={startCall} endCall={endCall} transcriptStep={transcriptStep} />
              <WhatsAppPanel messages={waMessages} draft={waDraft} setDraft={setWaDraft} onSend={sendWA} d={d} />
            </TabsContent>

            <TabsContent value="plan" className="mt-4 space-y-4">
              <div className="surface-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-primary">Payment plan builder</p>
                    <h3 className="text-base font-semibold mt-0.5">Configure installments</h3>
                  </div>
                  <Badge variant="outline" className="text-success border-success/40 bg-success/10">Within policy</Badge>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-5">
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-muted-foreground">Down payment</span>
                        <span className="font-mono-num font-semibold">{downPct}% • {formatSAR(downPayment)}</span>
                      </div>
                      <Slider value={[downPct]} onValueChange={(v) => setDownPct(v[0])} min={0} max={50} step={5} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-muted-foreground">Installments</span>
                        <span className="font-mono-num font-semibold">{installments} months</span>
                      </div>
                      <Slider value={[installments]} onValueChange={(v) => setInstallments(v[0])} min={2} max={18} step={1} />
                    </div>
                    <div className="p-3 rounded-lg bg-muted/60 border border-border">
                      <p className="text-xs text-muted-foreground">Monthly installment</p>
                      <p className="font-mono-num text-2xl font-semibold mt-0.5 text-foreground">{formatSAR(monthly)}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">Total {formatSAR(downPayment + monthly * installments)} over {installments + 1} payments</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-1">Installment schedule</p>
                    <div className="max-h-60 overflow-y-auto space-y-1 pr-1 scrollbar-thin">
                      {Array.from({ length: installments }).map((_, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-md bg-muted/60 border border-border">
                          <span className="text-muted-foreground">Installment {i + 1}</span>
                          <span>{formatDate(new Date(Date.now() + (i + 1) * 30 * 86400000).toISOString())}</span>
                          <span className="font-mono-num font-semibold">{formatSAR(monthly)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center gap-2">
                  <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={createPTP}><Bookmark className="h-4 w-4 mr-1.5" />Record PTP</Button>
                  <Button size="sm" variant="outline" onClick={sendPaymentLink}><CreditCard className="h-4 w-4 mr-1.5" />Generate SADAD link</Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground">Request supervisor approval</Button>
                  <span className="ml-auto text-[11px] text-muted-foreground">Plan auto-approved — within standard policy thresholds</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <Timeline caseId={d.caseId} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right rail: AI assistant + history */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="surface-card p-5 bg-gradient-to-br from-onyx to-onyx/95 text-background">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center"><Bot className="h-3.5 w-3.5 text-primary" /></div>
              <p className="text-[10px] uppercase tracking-widest text-primary">AI assistant</p>
              <Badge variant="outline" className="ml-auto text-[10px] border-primary/40 text-primary bg-primary/10">Confidence 87%</Badge>
            </div>
            <p className="text-sm leading-relaxed text-background/85">
              Debtor has reliable contactability with 2 prior promises kept. Recommend a 6-month plan at 10% down (SAR {monthly.toLocaleString()} / mo) and a WhatsApp settlement link.
              Avoid offering discount above 8% — monthly cash flow analysis suggests full recovery is viable.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              {['Use Arabic script', 'Offer 6-month plan', 'WhatsApp first', 'Avoid call after 21:00'].map((t) => (
                <div key={t} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white/5 border border-white/10"><CheckCheck className="h-3 w-3 text-primary" />{t}</div>
              ))}
            </div>
          </div>

          <div className="surface-card p-5">
            <p className="text-[10px] uppercase tracking-widest text-primary">Contact history</p>
            <ul className="mt-3 space-y-3 text-sm">
              {[
                { ch: 'Call', who: 'Layla B.', tag: 'RPC', when: '2d ago' },
                { ch: 'WhatsApp', who: 'Fares S.', tag: 'Delivered', when: '5d ago' },
                { ch: 'SMS', who: 'System', tag: 'Sent', when: '11d ago' },
                { ch: 'Call', who: 'Sara Q.', tag: 'No answer', when: '18d ago' },
              ].map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="h-7 w-7 rounded-md bg-muted text-muted-foreground flex items-center justify-center">
                    {h.ch === 'Call' ? <Phone className="h-3 w-3" /> : h.ch === 'WhatsApp' ? <MessageCircle className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                  </span>
                  <div className="flex-1">
                    <p className="text-xs"><span className="font-medium">{h.ch}</span> by {h.who}</p>
                    <p className="text-[11px] text-muted-foreground">{h.tag} • {h.when}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

const ScoreBar = ({ label, value, tone = 'primary' }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono-num font-semibold">{value}</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div className={cn('h-full', tone === 'primary' && 'bg-primary', tone === 'success' && 'bg-success', tone === 'info' && 'bg-info', tone === 'destructive' && 'bg-destructive')} style={{ width: `${value}%` }} />
    </div>
  </div>
);

const CallConsole = ({ d, callActive, callSeconds, muted, setMuted, startCall, endCall, transcriptStep }) => {
  const mm = Math.floor(callSeconds / 60).toString().padStart(2, '0');
  const ss = (callSeconds % 60).toString().padStart(2, '0');
  const voiceSupported = isVoiceSupported();
  return (
    <div className="surface-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-onyx text-background flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn('h-10 w-10 rounded-full flex items-center justify-center', callActive ? 'bg-success/20 text-success' : 'bg-white/10 text-background')}>
            {callActive ? <Headphones className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Telephony console <span className="badge-mock ml-1">mock</span></p>
            <p className="text-sm font-medium">{d.mobileMasked} • {d.name}</p>
          </div>
          <div className="hidden md:flex items-center gap-1 ml-3 pl-3 border-l border-white/10">
            <Bot className="h-3.5 w-3.5 text-primary" />
            <div className="leading-tight">
              <p className="text-[9px] uppercase tracking-widest text-background/55">AI voice</p>
              <p className="text-[11px] font-medium text-background">{describeVoice({ nationality: d.nationality, gender: d.gender })}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {callActive && (
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
              </span>
              <span className="text-xs text-destructive font-medium">REC</span>
              <span className="font-mono-num text-sm text-background/80">{mm}:{ss}</span>
            </div>
          )}
          <ComplianceBadge status="allowed" message="Window OK • RPC" className="!text-[10px]" />
        </div>
      </div>

      <div className="grid md:grid-cols-3">
        <div className="md:col-span-2 p-5 border-r border-border min-h-[260px]">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Live transcript</p>
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            <AnimatePresence>
              {SCRIPT_LINES.slice(0, transcriptStep).map((l, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className={cn('flex gap-2 text-sm', l.who === 'debtor' ? 'flex-row-reverse' : '')}>
                  <span className={cn('mt-1 inline-flex h-5 px-1.5 items-center text-[10px] rounded font-semibold uppercase', l.who === 'agent' ? 'bg-primary/15 text-gold-deep' : 'bg-muted text-muted-foreground')}>{l.who === 'agent' ? 'Agent' : 'Debtor'}</span>
                  <p className={cn('max-w-[80%] px-3 py-1.5 rounded-lg text-sm', l.who === 'agent' ? 'bg-card border border-border' : 'bg-primary/10 border border-primary/30 text-foreground')}>{l.text}</p>
                </motion.div>
              ))}
            </AnimatePresence>
            {!callActive && (
              <div className="text-xs text-muted-foreground py-8 text-center">
                Click <span className="text-primary font-medium">Start call</span> to begin a mock conversation with live AI voice playback.
                {!voiceSupported && (
                  <p className="mt-2 text-warning text-[11px]">Your browser does not support audio playback — transcript will stream silently.</p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Call controls</p>
          {!callActive ? (
            <ComplianceGate verdict={evaluateAction(d, 'call')} className="w-full">
              <Button onClick={startCall} className="w-full bg-success text-success-foreground hover:bg-success/90" data-testid="debtor360-start-call">
                <Phone className="h-4 w-4 mr-1.5" /> Start mock call
              </Button>
            </ComplianceGate>
          ) : (
            <div className="space-y-2">
              <Button onClick={endCall} className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <PhoneOff className="h-4 w-4 mr-1.5" /> End call
              </Button>
              <Button variant="outline" onClick={() => setMuted(!muted)} className="w-full">
                {muted ? <><Mic className="h-4 w-4 mr-1.5" />Unmute</> : <><MicOff className="h-4 w-4 mr-1.5" />Mute</>}
              </Button>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground pt-2">Script guidance</p>
          <ul className="text-xs text-foreground space-y-1.5">
            <li className="flex items-start gap-1.5"><CheckCheck className="h-3.5 w-3.5 text-success mt-0.5" />Confirm RPC before discussing balance</li>
            <li className="flex items-start gap-1.5"><CheckCheck className="h-3.5 w-3.5 text-success mt-0.5" />Use Arabic salutation</li>
            <li className="flex items-start gap-1.5"><CheckCheck className="h-3.5 w-3.5 text-success mt-0.5" />Offer 6–9 month plan</li>
            <li className="flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5" />Do not exceed 8% discount</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const WhatsAppPanel = ({ messages, draft, setDraft, onSend, d }) => {
  return (
    <div className="surface-card overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-success/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-success/20 text-success flex items-center justify-center"><MessageCircle className="h-4 w-4" /></div>
          <div>
            <p className="text-sm font-semibold">WhatsApp • {d.mobileMasked}</p>
            <p className="text-[11px] text-success">Within 24h conversation window • Template-free</p>
          </div>
        </div>
        <span className="badge-mock">mock cloud API</span>
      </div>
      <div className="p-4 bg-[hsl(40_25%_96%)] min-h-[200px] max-h-72 overflow-y-auto space-y-2 scrollbar-thin">
        {messages.map((m, i) => (
          m.who === 'system' ? (
            <div key={i} className="text-center text-[11px] text-muted-foreground py-1">{m.text}</div>
          ) : (
            <div key={i} className={cn('flex', m.who === 'agent' ? 'justify-end' : 'justify-start')}>
              <div className={cn('max-w-[75%] px-3 py-1.5 rounded-2xl text-sm shadow-sm', m.who === 'agent' ? 'bg-primary/15 border border-primary/30 rounded-br-sm' : 'bg-card border border-border rounded-bl-sm')}>
                <p>{m.text}</p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  <span className="text-[10px] text-muted-foreground">{m.ts}</span>
                  {m.state === 'read' && <CheckCheck className="h-3 w-3 text-info" />}
                  {m.state === 'delivered' && <CheckCheck className="h-3 w-3 text-muted-foreground" />}
                  {m.state === 'sent' && <CheckCheck className="h-3 w-3 text-muted-foreground opacity-50" />}
                </div>
              </div>
            </div>
          )
        ))}
      </div>
      <div className="p-3 border-t border-border flex items-center gap-2">
        <Input placeholder="Type Arabic or English message…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSend()} className="flex-1" />
        <Button onClick={onSend} size="sm" className="bg-success hover:bg-success/90 text-success-foreground"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const Timeline = ({ caseId }) => {
  const events = [
    { t: '2 min ago', title: 'Promise to pay created', body: 'SAR 1,500 first installment due in 7 days', kind: 'success' },
    { t: '12 min ago', title: 'WhatsApp — Payment plan link sent', body: 'SAD-775913 • delivered & read', kind: 'info' },
    { t: '18 min ago', title: 'Call ended', body: '3m 42s • disposition Right-Party Contact • transcript stored', kind: 'info' },
    { t: '2 days ago', title: 'Case re-assigned', body: 'To Layla Bin Ali — reason: AGENT_HAS_CAPACITY, ARABIC_LANGUAGE_MATCH', kind: 'muted' },
    { t: '4 days ago', title: 'SIMAH enrichment', body: 'Risk band B2 • no default flag', kind: 'muted' },
    { t: '6 days ago', title: 'Imported from ERP', body: 'Source: Saudi Retail Bank — PRT-2026-014', kind: 'muted' },
  ];
  return (
    <div className="surface-card p-5">
      <p className="text-[10px] uppercase tracking-widest text-primary mb-3">Case timeline — {caseId}</p>
      <div className="relative">
        <div className="absolute left-3 top-1 bottom-1 w-px bg-border" />
        <ul className="space-y-4">
          {events.map((e, i) => (
            <li key={i} className="relative pl-9">
              <span className={cn('absolute left-1.5 top-1.5 h-3 w-3 rounded-full ring-2 ring-background', e.kind === 'success' ? 'bg-success' : e.kind === 'info' ? 'bg-primary' : 'bg-muted-foreground/50')} />
              <p className="text-sm font-medium">{e.title}</p>
              <p className="text-xs text-muted-foreground">{e.body}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{e.t}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
